import React, { useEffect, useState } from "react";
import { Box, Tabs, Tab, Typography } from "@mui/material";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import api from "../../../services/Axios";
import toast from "react-hot-toast";
import { Helmet } from "react-helmet";

function CustomTabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ mt: 2 }}>{children}</Box> : null;
}

export default function PrivacyTerms() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [privacyId, setPrivacyId] = useState(null);
  const [privacyHtml, setPrivacyHtml] = useState("");

  const [termsId, setTermsId] = useState(null);
  const [termsHtml, setTermsHtml] = useState("");

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
  });

  // =========================
  // API CALLS
  // =========================
  const getPrivacyList = () =>
    api.get("/privacy-policy", getAuthHeader());

  const createPrivacy = (payload) =>
    api.post("/privacy-policy", payload, getAuthHeader());

  const updatePrivacy = (id, payload) =>
    api.patch(`/privacy-policy/${id}`, payload, getAuthHeader());

  const getTermsList = () =>
    api.get("/terms-condition", getAuthHeader());

  const createTerms = (payload) =>
    api.post("/terms-condition", payload, getAuthHeader());

  const updateTerms = (id, payload) =>
    api.patch(`/terms-condition/${id}`, payload, getAuthHeader());

  // =========================
  // LOAD DATA
  // =========================
  const pickLatest = (list = []) => {
    if (!list.length) return null;
    return [...list].sort(
      (a, b) =>
        new Date(b.updatedAt || b.createdAt || 0) -
        new Date(a.updatedAt || a.createdAt || 0)
    )[0];
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [pRes, tRes] = await Promise.all([
        getPrivacyList(),
        getTermsList(),
      ]);

      if (pRes?.data?.success) {
        const latest = pickLatest(pRes.data.data);
        setPrivacyId(latest?.id || null);
        setPrivacyHtml(latest?.content || "");
      }

      if (tRes?.data?.success) {
        const latest = pickLatest(tRes.data.data);
        setTermsId(latest?.id || null);
        setTermsHtml(latest?.content || "");
      }
    } catch (e) {
      toast.error("Failed to load data");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  // =========================
  // SAVE
  // =========================
  const savePrivacy = async () => {
    if (!privacyHtml.trim()) {
      toast.error("Privacy content is required");
      return;
    }

    setSaving(true);
    const payload = { content: privacyHtml };

    const res = privacyId
      ? await updatePrivacy(privacyId, payload)
      : await createPrivacy(payload);

    setSaving(false);

    if (res?.data?.success || res?.status === 200) {
      toast.success("Privacy Policy saved");
      loadData();
    } else {
      toast.error("Failed to save");
    }
  };

  const saveTerms = async () => {
    if (!termsHtml.trim()) {
      toast.error("Terms content is required");
      return;
    }

    setSaving(true);
    const payload = {
      title: "Terms & Conditions",
      content: termsHtml,
    };

    const res = termsId
      ? await updateTerms(termsId, payload)
      : await createTerms(payload);

    setSaving(false);

    if (res?.data?.success || res?.status === 200) {
      toast.success("Terms & Conditions saved");
      loadData();
    } else {
      toast.error("Failed to save");
    }
  };

  // =========================
  // EDITOR CONFIG
  // =========================
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link"],
      ["clean"],
    ],
  };

  return (
    <div className="listingPages allAccountsPage">
       <Helmet>
        <title>CMS</title>
      </Helmet>
      <Box maxWidth={1200} mx="auto">
        <div className="listingPagesHead">
          <div className="left">
            <h3>CMS Management</h3>
          </div>
        </div>

        <div className="listingMain">
          <Box sx={{ borderBottom: 1, borderColor: "#0057FF" }}>
            <Tabs value={tab} onChange={(e, v) => setTab(v)}>
              <Tab label="Privacy Policy" />
              <Tab label="Terms & Conditions" />
            </Tabs>
          </Box>

          {loading && (
            <Typography sx={{ color: "#fff", mt: 2 }}>
              Loading...
            </Typography>
          )}

          {/* ================= Privacy ================= */}
          <CustomTabPanel value={tab} index={0}>
            {/* <Typography sx={{ color: "#fff", mb: 1 }}>
              Edit Privacy Policy
            </Typography> */}

            <ReactQuill
              value={privacyHtml}
              onChange={setPrivacyHtml}
              modules={quillModules}
              theme="snow"
              style={{
                background: "#0E243F",
                color: "#fff",
                borderRadius: 8,
                minHeight: 300,
                marginBottom: 20,
              }}
            />

            <button
              className="themeBtn fullBtn"
              onClick={savePrivacy}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </CustomTabPanel>

          {/* ================= Terms ================= */}
          <CustomTabPanel value={tab} index={1}>
            {/* <Typography sx={{ color: "#fff", mb: 1 }}>
              Edit Terms & Conditions
            </Typography> */}

            <ReactQuill
              value={termsHtml}
              onChange={setTermsHtml}
              modules={quillModules}
              theme="snow"
              style={{
                background: "#0E243F",
                color: "#fff",
                borderRadius: 8,
                minHeight: 300,
                marginBottom: 20,
              }}
            />

            <button
              className="themeBtn fullBtn"
              onClick={saveTerms}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </CustomTabPanel>
        </div>
      </Box>

      {/* ================= INLINE DARK THEME FOR QUILL ================= */}
      <style>
        {`
          .ql-toolbar.ql-snow {
            background: #0E243F;
            border-color: rgba(255,255,255,0.2);
          }
          .ql-container.ql-snow {
            background: #0E243F;
            border-color: rgba(255,255,255,0.2);
          }
          .ql-editor {
            min-height: 260px;
            color: #fff;
          }
          .ql-editor.ql-blank::before {
            color: rgba(255,255,255,0.4);
          }
          .ql-snow .ql-stroke {
            stroke: #fff;
          }
          .ql-snow .ql-fill {
            fill: #fff;
          }
          .ql-snow .ql-picker {
            color: #fff;
          }
        `}
      </style>
    </div>
  );
}
